import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import Footer from "../../components/ui/Footer";
import { clearCnmapCart } from "../../helpers/openInNewTab";

const LMS_LEARN_HOME_URL = "https://acma.docebosaas.com/learn";
const LMS_FORGOT_PASSWORD_URL =
  "https://acma.docebosaas.com/learn/forgot-password";

export default function SuccessPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    clearCnmapCart();
    try {
      sessionStorage.removeItem("cnmap_checkout_form");
      sessionStorage.removeItem("cnmap_checkout_auto_resume");
    } catch {
      // ignore
    }
    if (!sessionId || sessionId.length < 5) {
      setLoading(false);
      return;
    }

    let active = true;
    fetch("/api/stripe-success-session-id", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        setReceipt(data);
      })
      .catch(() => {
        if (!active) return;
        setReceipt({ success: false });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [sessionId]);

  const email = (receipt && receipt.data && receipt.data.email) || "";
  const customerName = (receipt && receipt.data && receipt.data.name) || "";
  const total =
    receipt && receipt.data && typeof receipt.data.total === "number"
      ? receipt.data.total
      : 649.0;
  const lineItems =
    (receipt &&
      receipt.data &&
      Array.isArray(receipt.data.line_items) &&
      receipt.data.line_items) || [
      {
        name: "The Certified Nurse Medical Affairs Professional (CNMAP™) Program",
        images: [
          "https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/acmaMarketplace/productImages/cnmap.svg",
        ],
        total: 649.0,
      },
    ];

  return (
    <>
      <Helmet>
        <title>Payment Confirmed | CNMAP™</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: "calc(100vh - 200px)",
          py: { xs: 5, md: 8 },
        }}
      >
        <Container maxWidth="md">
          {loading ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2, color: "#475569" }}>
                Verifying your enrollment & receipt...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "#dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 36, color: "#16a34a" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}
                >
                  Payment Successful
                </Typography>
                <Typography variant="body1" sx={{ color: "#475569" }}>
                  Thank you for your purchase. You&apos;ll receive a
                  confirmation email shortly.
                </Typography>
              </Box>

              <Card
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Payment Summary Header */}
                <Box
                  sx={{
                    background:
                      "linear-gradient(90deg, #ffffff 0%, #f1f5f9 100%)",
                    borderBottom: "1px solid #e2e8f0",
                    p: { xs: 3, sm: 4 },
                  }}
                >
                  <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item xs={12} sm={7}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}
                      >
                        Payment Details
                      </Typography>
                      {customerName ? (
                        <Typography variant="body2" sx={{ color: "#334155" }}>
                          <b>Name:</b> {customerName}
                        </Typography>
                      ) : null}
                      {email ? (
                        <Typography variant="body2" sx={{ color: "#334155" }}>
                          <b>Email:</b> {email}
                        </Typography>
                      ) : null}
                      {sessionId ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "#64748b",
                            wordBreak: "break-all",
                          }}
                        >
                          Reference ID: {sessionId}
                        </Typography>
                      ) : null}
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Box
                        sx={{
                          backgroundColor: "#ecfdf5",
                          border: "1px solid #a7f3d0",
                          borderRadius: "12px",
                          p: 2,
                          textAlign: { xs: "left", sm: "right" },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#047857", fontWeight: 700 }}
                        >
                          PAYMENT TOTAL
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 800, color: "#047857" }}
                        >
                          ${Number(total).toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Order Items & 3-Step LMS Getting Started Guide */}
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1e293b", mb: 2.5 }}
                  >
                    Your Order Items
                  </Typography>

                  {lineItems.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        p: 2.5,
                        mb: 3,
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2.5}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            backgroundColor: "#032d3a",
                            borderRadius: "10px",
                            p: 1.5,
                            width: { xs: "100%", sm: 140 },
                            height: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={
                              (item.images && item.images[0]) ||
                              "https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/acmaMarketplace/productImages/cnmap.svg"
                            }
                            alt={item.name}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Chip
                            icon={<ShoppingBagOutlinedIcon />}
                            label="Enrolled"
                            size="small"
                            color="success"
                            sx={{ mb: 1, fontWeight: 700 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
                          >
                            {item.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#475569", lineHeight: 1.6 }}
                          >
                            An ACMA Learning Management System account has been
                            created with your email. Check your inbox for a
                            temporary password.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}

                  <Divider sx={{ my: 3 }} />

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
                  >
                    Getting Started
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#475569", mb: 2.5 }}
                  >
                    Follow these steps to access your course and start learning:
                  </Typography>

                  <Stack spacing={2}>
                    <Box
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        p: 2.5,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack direction="row" spacing={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          1
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "#0f172a" }}
                          >
                            Email Confirmation
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#475569", mt: 0.5 }}
                          >
                            Check your inbox at{" "}
                            <b>{email || "the email used during purchase"}</b>{" "}
                            for a payment confirmation email containing your
                            ACMA login credentials and temporary password.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        p: 2.5,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack direction="row" spacing={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          2
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "#0f172a" }}
                          >
                            Learning Management System Access
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#475569", mt: 0.5, mb: 1.5 }}
                          >
                            If you didn&apos;t receive the temporary password
                            email, you can reset your LMS password directly:
                            username{" "}
                            <b>{email || "your purchase email"}</b> —{" "}
                            <Link
                              href={LMS_FORGOT_PASSWORD_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 700 }}
                            >
                              Reset Learning Management System Password
                            </Link>
                          </Typography>
                          <Button
                            variant="contained"
                            href={LMS_LEARN_HOME_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<ChevronRightIcon />}
                          >
                            Login to your course
                          </Button>
                        </Box>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        p: 2.5,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack direction="row" spacing={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          3
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "#0f172a" }}
                          >
                            Start Learning
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#475569", mt: 0.5 }}
                          >
                            Once logged into the{" "}
                            <Link
                              href={LMS_LEARN_HOME_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 700 }}
                            >
                              Learning Management System
                            </Link>
                            , you can begin your CNMAP™ modules immediately and
                            track your progress.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>

                <Box
                  sx={{
                    backgroundColor: "#f8fafc",
                    borderTop: "1px solid #e2e8f0",
                    p: 2.5,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Questions about your purchase? Contact our technical support
                    team at{" "}
                    <Link
                      href="mailto:info@acmainfo.org"
                      sx={{ fontWeight: 700 }}
                    >
                      info@acmainfo.org
                    </Link>{" "}
                    or{" "}
                    <Link component={RouterLink} to="/home" sx={{ fontWeight: 700 }}>
                      return to CNMAP Home
                    </Link>
                    .
                  </Typography>
                </Box>
              </Card>
            </>
          )}
        </Container>
      </Box>

      <Footer />
    </>
  );
}
